import React, { useState, useRef } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Encounter, Patient, RadiologyRequest } from '../../types';
import { Scan, CheckCircle, Upload, X } from 'lucide-react';
// Biometry rows for the obstetric structured form
const BIOMETRY_ROWS = ['GS', 'CRL', 'BPD', 'HC', 'AC', 'FL', 'EFW'];
type ObstetricForm = {
  numberOfFetuses: string;
  presentation: string;
  afiDvp: string;
  fhr: string;
  fetalAnatomy: string;
  fetalMovements: string;
  cervixInternalOs: string;
  placentaA: string;
  placentaB: string;
  fetalA: {
    [key: string]: {
      size: string;
      ega: string;
    };
  };
  fetalB: {
    [key: string]: {
      size: string;
      ega: string;
    };
  };
  impression: string;
  sonographerName: string;
};
type PelvicForm = {
  findings: string;
  sonographerName: string;
};
const blankBiometry = () => BIOMETRY_ROWS.reduce((acc, row) => {
  acc[row] = {
    size: '',
    ega: ''
  };
  return acc;
}, {} as {
  [key: string]: {
    size: string;
    ega: string;
  };
});
export const RadiologyDashboard: React.FC = () => {
  const {
    encounters,
    patients,
    updateEncounter,
    currentUser
  } = useData();
  const [selectedRequest, setSelectedRequest] = useState<{
    enc: Encounter;
    rad: RadiologyRequest;
  } | null>(null);
  const [report, setReport] = useState('');
  const [images, setImages] = useState<string[]>([]);
  const [obstetricForm, setObstetricForm] = useState<ObstetricForm>({
    numberOfFetuses: '',
    presentation: '',
    afiDvp: '',
    fhr: '',
    fetalAnatomy: '',
    fetalMovements: '',
    cervixInternalOs: '',
    placentaA: '',
    placentaB: '',
    fetalA: blankBiometry(),
    fetalB: blankBiometry(),
    impression: '',
    sonographerName: ''
  });
  const [pelvicForm, setPelvicForm] = useState<PelvicForm>({
    findings: '',
    sonographerName: ''
  });
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Find encounters with pending radiology requests
  const pendingRequests = encounters.flatMap(enc => enc.radiologyRequests.filter(rad => rad.status === 'Pending').map(rad => ({
    enc,
    rad
  })));
  const getPatient = (patientId: string) => patients.find(p => p.id === patientId);
  const handleSelectRequest = (item: {
    enc: Encounter;
    rad: RadiologyRequest;
  }) => {
    setSelectedRequest(item);
    setReport('');
    setImages([]);
    setObstetricForm({
      numberOfFetuses: '',
      presentation: '',
      afiDvp: '',
      fhr: '',
      fetalAnatomy: '',
      fetalMovements: '',
      cervixInternalOs: '',
      placentaA: '',
      placentaB: '',
      fetalA: blankBiometry(),
      fetalB: blankBiometry(),
      impression: '',
      sonographerName: currentUser?.fullName || ''
    });
    setPelvicForm({
      findings: '',
      sonographerName: currentUser?.fullName || ''
    });
  };
  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(file => {
      // Check size (50MB limit as per requirements)
      if (file.size > 50 * 1024 * 1024) {
        alert('File exceeds 50MB limit');
        return;
      }
      const reader = new FileReader();
      reader.onload = event => {
        if (event.target?.result) {
          setImages(prev => [...prev, event.target!.result as string]);
        }
      };
      reader.readAsDataURL(file);
    });
  };
  const removeImage = (index: number) => {
    setImages(prev => prev.filter((_, i) => i !== index));
  };
  const buildObstetricReport = (f: ObstetricForm): string => {
    const lines: string[] = [];
    lines.push('OBSTETRIC ULTRASOUND REPORT');
    lines.push('');
    lines.push('FINDINGS');
    lines.push(`Number of Fetuses/GS: ${f.numberOfFetuses}`);
    lines.push(`Presentation: ${f.presentation}`);
    lines.push(`AFI/DVP: ${f.afiDvp}`);
    lines.push(`FHR: ${f.fhr}`);
    lines.push(`Fetal Anatomy: ${f.fetalAnatomy}`);
    lines.push(`Fetal Movements: ${f.fetalMovements}`);
    lines.push(`Cervix/Internal OS: ${f.cervixInternalOs}`);
    lines.push('');
    lines.push('BIOMETRY');
    lines.push(`Placenta (Fetal A): ${f.placentaA}`);
    lines.push(`Placenta (Fetal B): ${f.placentaB}`);
    lines.push('');
    lines.push('FETAL A:');
    BIOMETRY_ROWS.forEach(row => {
      lines.push(`  ${row}: Size=${f.fetalA[row].size || '-'}, EGA=${f.fetalA[row].ega || '-'}`);
    });
    lines.push('');
    lines.push('FETAL B:');
    BIOMETRY_ROWS.forEach(row => {
      lines.push(`  ${row}: Size=${f.fetalB[row].size || '-'}, EGA=${f.fetalB[row].ega || '-'}`);
    });
    lines.push('');
    lines.push(`IMPRESSION: ${f.impression}`);
    lines.push('');
    lines.push(`SONOGRAPHER'S NAME: ${f.sonographerName}`);
    return lines.join('\n');
  };
  const buildPelvicReport = (f: PelvicForm): string => {
    return ['ULTRASOUND REPORT', '', 'FINDINGS:', f.findings, '', `SONOGRAPHER'S NAME: ${f.sonographerName}`].join('\n');
  };
  const handleSaveResults = () => {
    if (!selectedRequest || !currentUser) return;
    const scanType = selectedRequest.rad.scanType;
    const isObstetric = scanType === 'OBS Scan' || scanType === 'Anomaly Scan';
    const isPelvic = scanType === 'Pelvic Scan';
    let finalReport = report;
    if (isObstetric) {
      finalReport = buildObstetricReport(obstetricForm);
    } else if (isPelvic) {
      finalReport = buildPelvicReport(pelvicForm);
    }
    if (!finalReport.trim() && images.length === 0) {
      alert('Please complete the report or upload images before sending.');
      return;
    }
    const {
      enc,
      rad
    } = selectedRequest;
    const updatedRad: RadiologyRequest = {
      ...rad,
      status: 'Completed',
      report: finalReport,
      images,
      completedAt: new Date().toISOString(),
      completedBy: currentUser.id,
      radiographerName: currentUser.fullName
    };
    const updatedRadRequests = enc.radiologyRequests.map(r => r.id === rad.id ? updatedRad : r);
    // Check if all rads are completed
    const allRadsCompleted = updatedRadRequests.every(r => r.status === 'Completed');
    const updateData: Partial<Encounter> = {
      radiologyRequests: updatedRadRequests,
      pendingRadiology: !allRadsCompleted
    };
    // If all rads done and no pending lab, move back to clinician
    if (allRadsCompleted && !enc.pendingLab) {
      updateData.stage = 'CLINICIAN';
    }
    updateEncounter(enc.id, updateData);
    alert('Scan results saved and sent to Clinician.');
    setSelectedRequest(null);
  };
  if (selectedRequest) {
    const patient = getPatient(selectedRequest.enc.patientId);
    if (!patient) return <div data-id="element-1542">Patient not found</div>;
    const scanType = selectedRequest.rad.scanType;
    const isObstetric = scanType === 'OBS Scan' || scanType === 'Anomaly Scan';
    const isPelvic = scanType === 'Pelvic Scan';
    const isSendDisabled = !isObstetric && !isPelvic ? !report && images.length === 0 : false;
    return <div className="space-y-6" data-id="element-1543">
        <div className="flex justify-between items-center" data-id="element-1544">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-1545">
            Radiology Results Entry
          </h1>
          <Button variant="outline" onClick={() => setSelectedRequest(null)} data-id="element-1546">
            Back to Queue
          </Button>
        </div>

        <Card className="bg-purple-50 border-purple-200" data-id="element-1547">
          <div className="flex justify-between items-start" data-id="element-1548">
            <div data-id="element-1549">
              <h2 className="text-xl font-bold text-gray-900" data-id="element-1550">
                {patient.firstName} {patient.lastName}
              </h2>
              <p className="text-sm text-gray-600" data-id="element-1551">
                BMC: {patient.bmcFileNumber} • {patient.age} yrs • {patient.sex}
              </p>
            </div>
            <div className="text-right" data-id="element-1552">
              <Badge variant="purple" className="text-sm" data-id="element-1553">
                {selectedRequest.rad.scanType}
              </Badge>
              <p className="text-xs text-gray-500 mt-1" data-id="element-1554">
                Requested:{' '}
                {new Date(selectedRequest.rad.requestedAt).toLocaleString()}
              </p>
            </div>
          </div>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-1555">
          <Card title="Upload Images" data-id="element-1556">
            <div className="space-y-4" data-id="element-1557">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:bg-gray-50 cursor-pointer" onClick={() => fileInputRef.current?.click()} data-id="element-1558">
                <Upload className="mx-auto h-12 w-12 text-gray-400 mb-2" data-id="element-1559" />
                <p className="text-sm text-gray-600" data-id="element-1560">
                  Click to upload images (PNG/JPG)
                </p>
                <p className="text-xs text-gray-500 mt-1" data-id="element-1561">
                  Max size: 50MB per image
                </p>
                <input type="file" ref={fileInputRef} className="hidden" accept="image/png, image/jpeg" multiple onChange={handleImageUpload} data-id="element-1562" />
              </div>

              {images.length > 0 && <div className="grid grid-cols-3 gap-4 mt-4" data-id="element-1563">
                  {images.map((img, idx) => <div key={idx} className="relative group" data-id="element-1564">
                      <img src={img} alt={`Scan ${idx}`} className="h-24 w-full object-cover rounded border border-gray-200" data-id="element-1565" />
                      <button onClick={() => removeImage(idx)} className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-1566">
                        <X className="w-4 h-4" data-id="element-1567" />
                      </button>
                    </div>)}
                </div>}
            </div>
          </Card>

          {/* Conditional report area */}
          {isObstetric ? <Card title="Obstetric Ultrasound Report" data-id="element-1568">
              <div className="space-y-4 text-sm" data-id="element-1569">
                <h3 className="font-bold text-center bg-gray-100 p-2 border border-black" data-id="element-1570">
                  OBSTETRIC ULTRASOUND REPORT
                </h3>

                <div className="border border-black" data-id="element-1571">
                  <div className="bg-gray-100 p-2 font-bold border-b border-black" data-id="element-1572">
                    FINDINGS
                  </div>
                  <div className="p-3 space-y-3" data-id="element-1573">
                    {[{
                  key: 'numberOfFetuses',
                  label: 'Number of Fetuses/GS'
                }, {
                  key: 'presentation',
                  label: 'Presentation'
                }, {
                  key: 'afiDvp',
                  label: 'AFI/DVP'
                }, {
                  key: 'fhr',
                  label: 'FHR'
                }, {
                  key: 'fetalAnatomy',
                  label: 'Fetal Anatomy'
                }, {
                  key: 'fetalMovements',
                  label: 'Fetal Movements'
                }, {
                  key: 'cervixInternalOs',
                  label: 'Cervix/Internal OS'
                }].map(field => <div key={field.key} data-id="element-1574">
                        <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1575">
                          {field.label}
                        </label>
                        <input type="text" value={(obstetricForm as any)[field.key]} onChange={e => setObstetricForm({
                    ...obstetricForm,
                    [field.key]: e.target.value
                  })} className="w-full border border-gray-300 rounded p-1.5 text-sm" data-id="element-1576" />
                      </div>)}
                  </div>
                </div>

                {/* BIOMETRY */}
                <div className="border border-black" data-id="element-1577">
                  <div className="bg-gray-100 p-2 font-bold border-b border-black" data-id="element-1578">
                    BIOMETRY
                  </div>
                  <div className="grid grid-cols-2 gap-2 p-3" data-id="element-1579">
                    <div data-id="element-1580">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1581">
                        Placenta (Fetal A)
                      </label>
                      <input type="text" value={obstetricForm.placentaA} onChange={e => setObstetricForm({
                    ...obstetricForm,
                    placentaA: e.target.value
                  })} className="w-full border border-gray-300 rounded p-1.5 text-sm" data-id="element-1582" />
                    </div>
                    <div data-id="element-1583">
                      <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1584">
                        Placenta (Fetal B)
                      </label>
                      <input type="text" value={obstetricForm.placentaB} onChange={e => setObstetricForm({
                    ...obstetricForm,
                    placentaB: e.target.value
                  })} className="w-full border border-gray-300 rounded p-1.5 text-sm" data-id="element-1585" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 px-3 pb-3" data-id="element-1586">
                    {(['fetalA', 'fetalB'] as const).map(side => <div key={side} data-id="element-1587">
                        <p className="font-bold text-center mb-1" data-id="element-1588">
                          {side === 'fetalA' ? 'FETAL A' : 'FETAL B'}
                        </p>
                        <table className="w-full border-collapse text-xs" data-id="element-1589">
                          <thead data-id="element-1590">
                            <tr className="bg-gray-100" data-id="element-1591">
                              <th className="border border-black p-1" data-id="element-1592"></th>
                              <th className="border border-black p-1" data-id="element-1593">Size</th>
                              <th className="border border-black p-1" data-id="element-1594">EGA</th>
                            </tr>
                          </thead>
                          <tbody data-id="element-1595">
                            {BIOMETRY_ROWS.map(row => <tr key={row} data-id="element-1596">
                                <td className="border border-black p-1 font-medium text-center" data-id="element-1597">
                                  {row}
                                </td>
                                <td className="border border-black p-0" data-id="element-1598">
                                  <input type="text" value={obstetricForm[side][row].size} onChange={e => setObstetricForm({
                            ...obstetricForm,
                            [side]: {
                              ...obstetricForm[side],
                              [row]: {
                                ...obstetricForm[side][row],
                                size: e.target.value
                              }
                            }
                          })} className="w-full p-1 text-xs" data-id="element-1599" />
                                </td>
                                <td className="border border-black p-0" data-id="element-1600">
                                  <input type="text" value={obstetricForm[side][row].ega} onChange={e => setObstetricForm({
                            ...obstetricForm,
                            [side]: {
                              ...obstetricForm[side],
                              [row]: {
                                ...obstetricForm[side][row],
                                ega: e.target.value
                              }
                            }
                          })} className="w-full p-1 text-xs" data-id="element-1601" />
                                </td>
                              </tr>)}
                          </tbody>
                        </table>
                      </div>)}
                  </div>
                </div>

                {/* IMPRESSION */}
                <div className="border border-black" data-id="element-1602">
                  <div className="bg-gray-100 p-2 font-bold border-b border-black" data-id="element-1603">
                    IMPRESSION
                  </div>
                  <textarea value={obstetricForm.impression} onChange={e => setObstetricForm({
                ...obstetricForm,
                impression: e.target.value
              })} className="w-full p-2 min-h-[100px] text-sm focus:outline-none" data-id="element-1604" />
                </div>

                <div className="border border-black p-3" data-id="element-1605">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1606">
                    SONOGRAPHER'S NAME
                  </label>
                  <input type="text" value={obstetricForm.sonographerName} onChange={e => setObstetricForm({
                ...obstetricForm,
                sonographerName: e.target.value
              })} className="w-full border-b border-black p-1 text-sm focus:outline-none" data-id="element-1607" />
                </div>
              </div>
            </Card> : isPelvic ? <Card title="Ultrasound Report" data-id="element-1608">
              <div className="space-y-4" data-id="element-1609">
                <h3 className="font-bold text-center bg-gray-100 p-2 border border-black" data-id="element-1610">
                  ULTRASOUND REPORT
                </h3>
                <div className="border border-black" data-id="element-1611">
                  <div className="bg-gray-100 p-2 font-bold border-b border-black" data-id="element-1612">
                    FINDINGS
                  </div>
                  <textarea value={pelvicForm.findings} onChange={e => setPelvicForm({
                ...pelvicForm,
                findings: e.target.value
              })} className="w-full p-3 min-h-[300px] text-sm focus:outline-none" placeholder="Enter detailed findings..." data-id="element-1613" />
                </div>
                <div className="border border-black p-3" data-id="element-1614">
                  <label className="block text-xs font-medium text-gray-700 mb-1" data-id="element-1615">
                    SONOGRAPHER'S NAME
                  </label>
                  <input type="text" value={pelvicForm.sonographerName} onChange={e => setPelvicForm({
                ...pelvicForm,
                sonographerName: e.target.value
              })} className="w-full border-b border-black p-1 text-sm focus:outline-none" data-id="element-1616" />
                </div>
              </div>
            </Card> : <Card title="Radiologist Report" data-id="element-1617">
              <div className="space-y-4 h-full flex flex-col" data-id="element-1618">
                <textarea className="w-full flex-1 min-h-[200px] border border-gray-300 rounded-md p-3 focus:ring-purple-500 focus:border-purple-500" placeholder="Enter detailed report findings here..." value={report} onChange={e => setReport(e.target.value)} data-id="element-1619" />
                <div className="text-sm text-gray-500 text-right" data-id="element-1620">
                  Report by: {currentUser?.fullName}
                </div>
              </div>
            </Card>}
        </div>

        <div className="flex justify-end" data-id="element-1621">
          <Button onClick={handleSaveResults} size="lg" className="bg-purple-600 hover:bg-purple-700 text-white" disabled={isSendDisabled} data-id="element-1622">
            <CheckCircle className="w-5 h-5 mr-2" data-id="element-1623" /> Send to Clinician
          </Button>
        </div>
      </div>;
  }
  return <div className="space-y-6" data-id="element-1624">
      <div className="flex justify-between items-center" data-id="element-1625">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-1626">
          Radiology Dashboard
        </h1>
        <Badge variant="purple" className="text-sm px-3 py-1" data-id="element-1627">
          {pendingRequests.length} Pending Scans
        </Badge>
      </div>

      <Card noPadding data-id="element-1628">
        <ul className="divide-y divide-gray-200" data-id="element-1629">
          {pendingRequests.length > 0 ? pendingRequests.map(item => {
          const patient = getPatient(item.enc.patientId);
          if (!patient) return null;
          return <li key={item.rad.id} className="p-4 hover:bg-gray-50 flex justify-between items-center" data-id="element-1630">
                  <div className="flex items-center" data-id="element-1631">
                    <div className="bg-purple-100 p-2 rounded-full mr-4" data-id="element-1632">
                      <Scan className="h-6 w-6 text-purple-600" data-id="element-1633" />
                    </div>
                    <div data-id="element-1634">
                      <p className="text-sm font-medium text-blue-600" data-id="element-1635">
                        {patient.bmcFileNumber}
                      </p>
                      <p className="text-lg font-bold text-gray-900" data-id="element-1636">
                        {patient.firstName} {patient.lastName}
                      </p>
                      <p className="text-sm font-medium text-purple-700" data-id="element-1637">
                        {item.rad.scanType}
                      </p>
                    </div>
                  </div>
                  <Button onClick={() => handleSelectRequest(item)} className="bg-purple-600 hover:bg-purple-700 text-white" data-id="element-1638">
                    Upload Scan
                  </Button>
                </li>;
        }) : <li className="p-12 text-center text-gray-500" data-id="element-1639">
              <Scan className="mx-auto h-12 w-12 text-gray-300 mb-4" data-id="element-1640" />
              <p className="text-lg font-medium text-gray-900" data-id="element-1641">
                No pending scan requests
              </p>
            </li>}
        </ul>
      </Card>
    </div>;
};